import React, { useEffect, useRef, useState } from 'react';
import chroma from 'chroma-js';

function rgbToHex(rgb) {
  // rgb can be 'rgb(r, g, b)' or 'rgba(r, g, b, a)'
  const result = rgb.match(/\d+/g);
  if (!result) return rgb;
  const r = parseInt(result[0], 10);
  const g = parseInt(result[1], 10);
  const b = parseInt(result[2], 10);
  return (
    '#' +
    [r, g, b]
      .map(x => {
        const hex = x.toString(16);
        return hex.length === 1 ? '0' + hex : hex;
      })
      .join('')
  );
}

function isColorLightHCL(rgbString, threshold = 60) {
  // chroma.js can parse 'rgb(r, g, b)' or hex directly
  const [h, c, l] = chroma(rgbString).hcl();
  return l > threshold; // threshold can be adjusted (e.g., 60)
}

const ColorSwatch = ({ value, className = '', children, useDarkMode }) => {
  const ref = useRef(null);
  const [cssValue, setCssValue] = useState('');
  const [isLight, setIsLight] = useState(false);

  useEffect(() => {
    if (ref.current) {
      // Defer to next tick to allow CSS to update
      const timeout = setTimeout(() => {
        const computed = getComputedStyle(ref.current).backgroundColor;
        setCssValue(rgbToHex(computed));
        setIsLight(isColorLightHCL(computed));
      }, 0);
      return () => clearTimeout(timeout);
    }
  }, [value, useDarkMode]);

  return (
    <div
      ref={ref}
      className={className}
      data-value={value}
      style={{ backgroundColor: `var(--slds-g-color-${value})`, color: isLight ? '#222' : '#fff' }}
    >
      {children && typeof children === 'string' ? children.replace(/<span className="display-value"><\/span>/g, '') : children}
      <span className="display-value" style={{ marginLeft: 8, fontSize: '0.9em', color: isLight ? '#222' : '#fff' }}>{cssValue}</span>
    </div>
  );
};

const ColorSystemInfo = ({ useDarkMode }) => (
    <div className="styling-hooks">
        <h1 className="slds-text-heading_large"> Salesforce Design System Global Styling Hooks</h1>
        <h3 className="slds-text-heading_small">Surface Colors</h3>
        <div className="ui-colors">
        <div className="surface-colors">
            <div className="on-surface">
            <ColorSwatch useDarkMode={useDarkMode}  value="on-surface-1">on-surface-1</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="on-surface-2">on-surface-2</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="on-surface-3">on-surface-3</ColorSwatch>
            <ColorSwatch className="border-1" useDarkMode={useDarkMode} value="border-1">border-1</ColorSwatch>
            <ColorSwatch className="border-2"  useDarkMode={useDarkMode} value="border-2">border-2</ColorSwatch>
            </div>
            <div className="surfaces">
            <ColorSwatch className="surface-1" useDarkMode={useDarkMode} value="surface-1">surface-1</ColorSwatch>
            <ColorSwatch className="surface-2" useDarkMode={useDarkMode} value="surface-2">surface-2</ColorSwatch>
            <ColorSwatch className="surface-3" useDarkMode={useDarkMode} value="surface-3">surface-3</ColorSwatch>
            </div>
            <div className="surface-containers">
            <ColorSwatch className="surface-container-1" useDarkMode={useDarkMode}  value="surface-container-1">surface-container-1</ColorSwatch>
            <ColorSwatch className="surface-container-2" useDarkMode={useDarkMode}  value="surface-container-2">surface-container-2</ColorSwatch>
            <ColorSwatch className="surface-container-3" useDarkMode={useDarkMode}  value="surface-container-3">surface-container-3</ColorSwatch>
            </div>
        </div>
        <h3 className="slds-text-heading_small">Accent Colors</h3>
        <div className="accent-colors">
            <div className="accents">
            <ColorSwatch className="accent-1" useDarkMode={useDarkMode}  value="accent-1">accent-1</ColorSwatch>
            <ColorSwatch className="accent-2" useDarkMode={useDarkMode}  value="accent-2">accent-2</ColorSwatch>
            <ColorSwatch className="accent-3" useDarkMode={useDarkMode}  value="accent-3">accent-3</ColorSwatch>
            </div>
            <div className="accent-containers">
            <ColorSwatch className="accent-container-1" useDarkMode={useDarkMode} value="accent-container-1">accent-container-1</ColorSwatch>
            <ColorSwatch className="accent-container-2" useDarkMode={useDarkMode} value="accent-container-2">accent-container-2</ColorSwatch>
            <ColorSwatch className="accent-container-3" useDarkMode={useDarkMode} value="accent-container-3">accent-container-3</ColorSwatch>
            </div>
            <div className="on-accents">
            <ColorSwatch useDarkMode={useDarkMode} value="on-accent-1">on-accent-1</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode} value="on-accent-2">on-accent-2</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode} value="on-accent-3">on-accent-3</ColorSwatch>
            </div>
            <div className="borders">
            <ColorSwatch useDarkMode={useDarkMode} value="border-accent-1">border-accent-1</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode} value="border-accent-2">border-accent-2</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode} value="border-accent-3">border-accent-3</ColorSwatch>
            </div>
        </div>
        <h3 className="slds-text-heading_small">Feedback Colors</h3>
        <h4 className="slds-text-title">Error Colors</h4>
        <div className="feedback-colors">
            <div className="error">
            <ColorSwatch useDarkMode={useDarkMode} value="error-1">error-1</ColorSwatch>
            </div>
            <div className="error-containers">
            <ColorSwatch useDarkMode={useDarkMode}  value="error-container-1">error-container-1</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="error-container-2">error-container-2</ColorSwatch>
            </div>
            <div className="on-error">
            <ColorSwatch useDarkMode={useDarkMode}  value="on-error-1">on-error-1</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="on-error-2">on-error-2</ColorSwatch>
            </div>
            <div className="border-error">
            <ColorSwatch useDarkMode={useDarkMode}  value="border-error-1">border-error-1</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="border-error-2">border-error-2</ColorSwatch>
            </div>
        </div>
        <h4 className="slds-text-title">Warning Colors</h4>
        <div className="feedback-colors">
            <div className="warning">
            <ColorSwatch useDarkMode={useDarkMode}  value="warning-1">warning-1</ColorSwatch>
            </div>
            <div className="warning-containers">
            <ColorSwatch useDarkMode={useDarkMode}  value="warning-container-1">warning-container-1</ColorSwatch>
            </div>
            <div className="on-warning">
            <ColorSwatch useDarkMode={useDarkMode}  value="on-warning-1">on-warning-1</ColorSwatch>
            </div>
            <div className="border-warning">
            <ColorSwatch useDarkMode={useDarkMode}  value="border-warning-1">border-warning-1</ColorSwatch>
            </div>
        </div>
        <h4 className="slds-text-title">Success Colors</h4>
        <div className="feedback-colors">
            <div className="success">
            <ColorSwatch useDarkMode={useDarkMode}  value="success-1">success-1</ColorSwatch>

            </div>
            <div className="success-containers">
            <ColorSwatch useDarkMode={useDarkMode}  value="success-container-1">success-container-1</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="success-container-2">success-container-2</ColorSwatch>
            </div>
            <div className="on-success">
            <ColorSwatch useDarkMode={useDarkMode}  value="on-success-1">on-success-1</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="on-success-2">on-success-2</ColorSwatch>
            </div>
            <div className="border-success">
            <ColorSwatch useDarkMode={useDarkMode}  value="border-success-1">border-success-1</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="border-success-2">border-success-2</ColorSwatch>
            </div>
        </div>
        <h4 className="slds-text-title">Info Colors</h4>
        <div className="feedback-colors">
            <div className="info">
            <ColorSwatch useDarkMode={useDarkMode}  value="info-1">info-1</ColorSwatch>
            </div>
            <div className="info-containers">
            <ColorSwatch useDarkMode={useDarkMode}  value="info-container-1">info-container-1</ColorSwatch>
            </div>
            <div className="on-info">
            <ColorSwatch useDarkMode={useDarkMode}  value="on-info-1">on-info-1</ColorSwatch>
            </div>
        </div>
        <h4 className="slds-text-title">Disabled Colors</h4>
        <div className="feedback-colors">
            <div className="disabled">
            <ColorSwatch useDarkMode={useDarkMode}  value="disabled-1">disabled-1</ColorSwatch>
            </div>
            <div className="disabled-containers">
            <ColorSwatch useDarkMode={useDarkMode}  value="disabled-container-1">disabled-container-1</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="disabled-container-2">disabled-container-2</ColorSwatch>
            </div>
            <div className="on-disabled">
            <ColorSwatch useDarkMode={useDarkMode}  value="on-disabled-1">on-disabled-1</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="on-disabled-2">on-disabled-2</ColorSwatch>
            </div>
            <div className="border-disabled">
            <ColorSwatch useDarkMode={useDarkMode}  value="border-disabled-1">border-disabled-1</ColorSwatch>
            </div>
        </div>
        <h3 className="slds-text-heading_small">Accessible System Colors</h3>
        <h4 className="slds-text-title">Neutral Colors</h4>
        <div className="system-colors">
            <ColorSwatch useDarkMode={useDarkMode}  value="neutral-base-10">neutral-base-10</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="neutral-base-15">neutral-base-15</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="neutral-base-20">neutral-base-20</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="neutral-base-30">neutral-base-30</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="neutral-base-40">neutral-base-40</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="neutral-base-50">neutral-base-50</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="neutral-base-60">neutral-base-60</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="neutral-base-65">neutral-base-65</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="neutral-base-70">neutral-base-70</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="neutral-base-80">neutral-base-80</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="neutral-base-90">neutral-base-90</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="neutral-base-95">neutral-base-95</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="neutral-base-100">neutral-base-100</ColorSwatch>
        </div>
    
        <h4 className="slds-text-title">Brand Colors</h4>
        <div className="system-colors">
            <ColorSwatch useDarkMode={useDarkMode}  value="brand-base-10">brand-base-10</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="brand-base-15">brand-base-15</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="brand-base-20">brand-base-20</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="brand-base-30">brand-base-30</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="brand-base-40">brand-base-40</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="brand-base-50">brand-base-50</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="brand-base-60">brand-base-60</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="brand-base-65">brand-base-65</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="brand-base-70">brand-base-70</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="brand-base-80">brand-base-80</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="brand-base-90">brand-base-90</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="brand-base-95">brand-base-95</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="brand-base-100">brand-base-100</ColorSwatch>
        </div>
    
        <h4 className="slds-text-title">Error Colors</h4>
        <div className="system-colors feedback">
            <ColorSwatch useDarkMode={useDarkMode}  value="error-base-10">error-base-10</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="error-base-20">error-base-20</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="error-base-30">error-base-30</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="error-base-40">error-base-40</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="error-base-50">error-base-50</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="error-base-60">error-base-60</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="error-base-70">error-base-70</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="error-base-80">error-base-80</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="error-base-90">error-base-90</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="error-base-100">error-base-100</ColorSwatch>
        </div>
        <h4 className="slds-text-title">Warning Colors</h4>
        <div className="system-colors feedback">
            <ColorSwatch useDarkMode={useDarkMode}  value="warning-base-10">warning-base-10</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="warning-base-20">warning-base-20</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="warning-base-30">warning-base-30</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="warning-base-40">warning-base-40</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="warning-base-50">warning-base-50</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="warning-base-60">warning-base-60</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="warning-base-70">warning-base-70</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="warning-base-80">warning-base-80</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="warning-base-90">warning-base-90</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="warning-base-100">warning-base-100</ColorSwatch>
        </div>
        <h4 className="slds-text-title">Success Colors</h4>
        <div className="system-colors feedback">
            <ColorSwatch useDarkMode={useDarkMode}  value="success-base-10">success-base-10</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="success-base-20">success-base-20</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="success-base-30">success-base-30</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="success-base-40">success-base-40</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="success-base-50">success-base-50</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="success-base-60">success-base-60</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="success-base-70">success-base-70</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="success-base-80">success-base-80</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="success-base-90">success-base-90</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="success-base-100">success-base-100</ColorSwatch>
        </div>
        <h4 className="slds-text-title">Info Colors</h4>
        <div className="system-colors feedback">
            <ColorSwatch useDarkMode={useDarkMode}  value="info-base-10">info-base-10</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="info-base-20">info-base-20</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="info-base-30">info-base-30</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="info-base-40">info-base-40</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="info-base-50">info-base-50</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="info-base-60">info-base-60</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="info-base-70">info-base-70</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="info-base-80">info-base-80</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="info-base-90">info-base-90</ColorSwatch>
            <ColorSwatch useDarkMode={useDarkMode}  value="info-base-100">info-base-100</ColorSwatch>
        </div>
        </div>
    </div>
);

export default ColorSystemInfo;